
import LoadingCard from "@components/card/LoadingCard.component";
import { SimpleDataTable } from "@components/datatable/DataTable.component";
import PliableLoader from "@components/loaders/PliableLoader.component";
import DagDataLibrary from "@components/nav/DagDataLibrary.component";
import PipelineNodeDataTable from "@components/pipelineNodes/PIpelineNodeDataTable";

import PipelineNodeInfo from "@components/pipelineNodes/PipelineNodeInfo.component";
import PipelineNodeName from "@components/pipelineNodes/PipelineNodeName.component";
import PipelineNodeNotFound from "@components/pipelineNodes/PipelineNodeNotFound.component";
import PipelineNodeSingleRecord from "@components/pipelineNodes/PipelineNodeSingleRecord.component";
import PipelineNodeSubnav from "@components/pipelineNodes/PipelineNodeSubnav.component";
import { PipelineNode, PipelineNodeMeasure, usePipelineNodeRecordData, usePipelineNodeReportDrilldown } from "@models/pipelineNode";
import { useMeasures } from "@models/reportBuilder";

import PageStructure, { PageContent, PageContentInner, PageSidebar, Pane, PaneContent, PaneContentWithSubnav, PaneFooter } from "@pages/PageStructure.component";
import { ApiError } from "@services/api/api.service";
import { usePipelineNode, usePipelineNodeRelationships } from "@stores/data.store";
import { useMemo, useState } from "react";
import { Collapse } from "react-bootstrap";
import { useParams } from "react-router-dom";

interface MeasureDataProps {
    node: PipelineNode;
    recordUuid: string;
    measure_indices: number[];
    measureKey: string;
    data: Record<string, any>[];
    show: boolean;
    onToggle: () => any;
}

const MeasureData = (props: MeasureDataProps) => {
    const measures = useMeasures();


    const measureLabels = useMemo(() => {
        if (!measures.data) {
            return [];
        }

        if (props.node.measures) {
            return props.node.measures.filter((m: PipelineNodeMeasure, idx: number) => {
                return props.measure_indices.includes(idx);
            }).map(m => m.name);
        } else if (props.node.measure_ids) {
            return props.node.measure_ids.filter((m: string, idx: number) => props.measure_indices.includes(idx)).map(mid => {
                const measure = measures.data?.find(m => m.id == mid);
                return measure?.name || '';
            });
        }
        return [];
    }, [props.node.measures, props.node.measure_ids, measures.dataUpdatedAt]);

    const pipelineNodeId = useMemo(() => {
        if (props.node.measures && props.node.measures[props.measure_indices[0]]) {
            return props.node.measures[props.measure_indices[0]].pipeline_node_id;

        } else if (!measures.data || !props.node.measure_ids) {
            return '';
        }
        return measures.data.find(m => m.id == props.node.measure_ids![props.measure_indices[0]])?.pipeline_node_id || '';
    }, [props.node, measures.dataUpdatedAt]);

    // Only load the data if we're showing it. Kind of a hack but it works
    const data = usePipelineNodeReportDrilldown(props.node.id as string, props.recordUuid, props.measureKey, !props.show);


   
    return <div className="card mb-2">
        <div className="card-header">
        <div className="d-flex center-vertically pointer" onClick={props.onToggle}>
                <div className="flex-1">
                    <strong>
                        <PipelineNodeName
                            pipelineNodeId={pipelineNodeId}
                            plural
                        />
                        &nbsp; ({measureLabels.join(', ')}) 
            
                    </strong>
                </div>   
                <div>
                    
                    {props.show && <span className="text-36">
                        <i className="mdi mdi-menu-up"></i>
                    </span>}
                    {!props.show && <span className="text-36">
                        <i className="mdi mdi-menu-down"></i>
                    </span>}
                </div>
            </div>
            
        </div>
        <Collapse in={props.show}>

            <div className="card-body">
                <div style={{height: '500px'}}>
                    {data.isLoading && <LoadingCard/>}
                    {props.show && !data.isLoading && <PipelineNodeDataTable
                        pipelineNodeId={pipelineNodeId}
                        dataOverride={data.data?.data || []}
                    />}
                </div>
            </div>
        </Collapse>
    </div>


}

const PipelineNodeRecordPage = () => {
    const { pipelineNodeId, recordUuid } = useParams();
    const pipelineNode = usePipelineNode(pipelineNodeId as string);
    const recordData = usePipelineNodeRecordData(pipelineNodeId as string, recordUuid as string);
    
    const relationships = usePipelineNodeRelationships();

    const parents = useMemo(() => {
        if (!relationships.data || !pipelineNodeId) {
            return [];
        }
        return relationships.data.filter(r => r.child_node_id == pipelineNodeId);
    }, [relationships.dataUpdatedAt, pipelineNodeId]);

    const children = useMemo(() => {
        if (!relationships.data || !pipelineNodeId) {
            return [];
        }
        return relationships.data.filter(r => r.parent_node_id == pipelineNodeId);
    }, [relationships.dataUpdatedAt, pipelineNodeId]);

    const [activeRelationshipId, setActiveRelationshipId] = useState('');

    if (pipelineNode.status == 'error' && (pipelineNode.error as ApiError).code == 404) {
        return <PipelineNodeNotFound/>
    }
    return <>
                <PipelineNodeSubnav
                    pipelineNodeId={pipelineNodeId as string}
                >
                    
                </PipelineNodeSubnav>
                    <PageContentInner hasHeader>
                        <div className="p-4">
                            <div className="card">
                                <div className="card-body">
                                    <h2>Record Details: <code>{recordUuid}</code></h2>

                                    {(recordData.isLoading || pipelineNode.isLoading) &&  <PliableLoader/>}
                                    {!recordData.isLoading && !pipelineNode.isLoading && recordData.data && pipelineNode.data && <div>
                                        <div className="row">
                                            <div className="col-4">
                                                <div className="shadow-box p-3">
                                                    <h3>This Record's Data</h3>
                                                    <PipelineNodeSingleRecord
                                                            record={recordData.data.record}
                                                            pipelineNodeId={pipelineNodeId as string}
                                                            compact
                                                        />
                                                </div>
                                                

                                            </div>
                                            <div className="col-8">
                                                {pipelineNode.data && pipelineNode.data.node_type == 'DIMENSION' && parents.length == 0 && children.length == 0 && <>
                                                    <div className="card">
                                                        <div className="card-body">
                                                            <h3>Relationships</h3>
                                                            This entity has no relationships. <i className="mdi mdi-emoticon-sad-outline"></i>
                                                        </div>
                                                    </div>
                                                </>}
                                                {recordData.data && recordData.data.measures && recordData.data.measures.map((m, idx) => {
                                                    const key = `measure${idx}`;
                                                    return <MeasureData
                                                        node={pipelineNode.data!}
                                                        recordUuid={recordUuid as string}
                                                        measureKey={m.key}
                                                        measure_indices={m.measure_indices}
                                                        data={m.data}
                                                        show={activeRelationshipId == key}
                                                        onToggle={() => {
                                                            if (activeRelationshipId == key) {
                                                                setActiveRelationshipId('');
                                                            } else {
                                                                setActiveRelationshipId(key);
                                                            }
                                                        }}

                                                    />
                                                })}
                                                {parents.map(p => {
                                                    if (!recordData.data!.relationships!.hasOwnProperty(p.id as string)) {
                                                        return <></>;
                                                    }
                                                    const relData = recordData.data!.relationships![p.id as string];
                                                    
                                                    return <div className="shadow-box p-3 mb-2">
                                                        <div>
                                                            <div className="d-flex center-vertically pointer" onClick={() => {
                                                                if (activeRelationshipId == p.id as string) {
                                                                    setActiveRelationshipId('');
                                                                } else {
                                                                    setActiveRelationshipId(p.id as string);
                                                                }
                                                            }}>
                                                                <div className="flex-1">
                                                                    <h3 className="mb-0">
                                                                        Related <PipelineNodeName
                                                                            pipelineNodeId={p.parent_node_id}
                                                                            singular
                                                                        />
                                                                        &nbsp;{p.name ? ('(' + p.name + ')') : ''} 
                                                        
                                                                    </h3>
                                                                </div>   
                                                                <div>
                                                                    
                                                                    {activeRelationshipId == p.id as string && <span className="font-24">
                                                                        <i className="mdi mdi-menu-up"></i>
                                                                    </span>}
                                                                    {activeRelationshipId != p.id as string && <span className="font-24">
                                                                        <i className="mdi mdi-menu-down"></i>
                                                                    </span>}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {activeRelationshipId == p.id as string && <>
                                                            <div>
                                                                {relData === null && <>
                                                                    No parent
                                                                </>}
                                                                {relData !== null && <>
                                                                    <PipelineNodeSingleRecord
                                                                        record={relData[0]}
                                                                        pipelineNodeId={p.parent_node_id}
                                                                        includeLink
                                                                    />
                                                                </>}

                                                            </div>
                                                        
                                                        </>}
                                                        
                                                        
                                                    </div>
                                                })}
                                                {children.map(p => {
                                                    if (!recordData.data!.relationships!.hasOwnProperty(p.id as string)) {
                                                        return <></>;
                                                    }
                                                    const relData = recordData.data!.relationships![p.id as string];
                                                    return <div className="card mb-2">
                                                        <div className="card-header">
                                                        <div className="d-flex center-vertically pointer" onClick={() => {
                                                                if (activeRelationshipId == p.id as string) {
                                                                    setActiveRelationshipId('');
                                                                } else {
                                                                    setActiveRelationshipId(p.id as string);
                                                                }
                                                            }}>
                                                                <div className="flex-1">
                                                                    <strong>
                                                                        <PipelineNodeName
                                                                            pipelineNodeId={p.child_node_id}
                                                                            plural
                                                                        />
                                                                        &nbsp;{p.name ? ('(' + p.name + ')') : ''} 
                                                        
                                                                    </strong>
                                                                </div>   
                                                                <div>
                                                                    
                                                                    {activeRelationshipId == p.id as string && <span className="text-36">
                                                                        <i className="mdi mdi-menu-up"></i>
                                                                    </span>}
                                                                    {activeRelationshipId != p.id as string && <span className="text-36">
                                                                        <i className="mdi mdi-menu-down"></i>
                                                                    </span>}
                                                                </div>
                                                            </div>
                                                            
                                                        </div>
                                                        <Collapse in={activeRelationshipId == p.id as string}>

                                                            <div className="card-body">
                                                                {relData === null && <>
                                                                    No children
                                                                </>}
                                                                {relData !== null && <div style={{height: '500px'}}>
                                                                    <PipelineNodeDataTable
                                                                        pipelineNodeId={p.child_node_id}
                                                                        dataOverride={relData}
                                                                    />
                                                                </div>}
                                                            </div>
                                                        </Collapse>
                                                    </div>
                                                })}
                                            </div>
                                        </div>
                                    </div>}
                                </div>
                            </div>
                            
                            
                        </div>
                                
                </PageContentInner>
        
                
            
        </>

}

export default PipelineNodeRecordPage;